import ReactDOM from 'react-dom';
import { MetricMetadataModalProps } from '../services/types';
import LoadingIndicator from './LoadingIndicator';
// import RaceEthnicityMetadataChart from './charts/RaceEthnicityMetadataChart';
import { buildLinkLabel, capitalizeFirstLetter, formatValue, parseCommaSeparatedUrls } from '../utils/helpers';
import { useEffect, useRef, useState } from 'react';
import DistributionChart from './charts/DistributionChart';
import metadataIconWhite from '../assets/metric-metadata-icon-white.svg';
import { skipHistogram } from '../utils/chartUtils';

const FOCUSABLE_SELECTOR = [
    'a[href]',
    'area[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    'summary',
    '[tabindex]:not([tabindex="-1"])'
].join(',');


const MetricMetadataModal: React.FC<MetricMetadataModalProps> = ({
    isOpen, 
    onClose, 
    id, 
    metadata, 
    distributionData, 
    countyDistributionData, 
    selectionActive, 
    loading, 
    error,
    triggerRef,
    modalId = 'metric-metadata-modal'
}) => {

    // live region announcement for SRs
    const [announcement, setAnnouncement] = useState('');
    const containerRef = useRef<HTMLDivElement | null>(null);
    const headingRef = useRef<HTMLHeadingElement | null>(null); // initial focus target
    const descriptionRef = useRef<HTMLParagraphElement | null>(null);
    const wasOpenRef = useRef(false);

    // Hide main scrollbar & key handlers
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                e.stopPropagation();
                onClose();
            } else if (e.key === 'Tab' && containerRef.current) {
                // trap focus within modal
                const focusables = Array.from(containerRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR))
                .filter(el => !el.hasAttribute('disabled') && el.tabIndex !== -1);
                if (focusables.length === 0) {
                e.preventDefault();
                return;
                }
                const first = focusables[0];
                const last = focusables[focusables.length - 1];
                const active = document.activeElement as HTMLElement | null;

                if (!e.shiftKey && active === last) {
                e.preventDefault();
                first.focus();
                } else if (e.shiftKey && active === first) {
                e.preventDefault();
                last.focus();
                }
            }
        };

        if (isOpen) {
            document.addEventListener('keydown', handleKeyDown, true);
            document.body.style.overflow = 'hidden';
            // SR announcement + set focus to heading on open
            setAnnouncement('Metric details dialog opened. Press Escape to close.');
            // Delay focus slightly to ensure nodes mounted
            setTimeout(() => {
                headingRef.current?.focus();
            }, 0);
            } else {
            document.body.style.overflow = '';
        }

        return () => {
            document.removeEventListener('keydown', handleKeyDown, true);
            document.body.style.overflow = '';
        };
    }, [isOpen, onClose]);

    // Return focus to trigger when closing
    useEffect(() => {
        const wasOpen = wasOpenRef.current;

        // Only return focus if the modal was open and is now closed.
        if (wasOpen && !isOpen && triggerRef?.current) {
            // Defer to next tick to ensure layout is settled
            setTimeout(() => triggerRef.current?.focus(), 0);
        }

        wasOpenRef.current = isOpen;
    }, [isOpen, triggerRef]);

    if (!isOpen) return null;

    const metricName = metadata ? metadata[0].metric_name : '';
    const lod = metadata ? metadata[0].versions[0].level_of_detail : '';

    // Define selected geography average distribution value and unit
    const distribution = distributionData ? distributionData[0] : null;
    const dataEntries = distribution?.versions[0]?.data || [];

    // Calculate geoAverage: Sum all values and divide by the count
    const geoAverage =
    dataEntries.length > 0
        ? dataEntries.reduce((sum, entry) => sum + entry.value, 0) / dataEntries.length
        : 0;

    const unit = distribution?.versions[0].unit || 'integer';
    const unitDescriptor = distribution?.versions[0].unit_descriptor || '';  

    // Define county average distribution value and unit
    const countyDistribution = countyDistributionData ? countyDistributionData[0] : null;
    const countyAverage = countyDistribution?.versions[0].avg_value || 0;
    const cUnit = countyDistribution?.versions[0].unit || 'integer';

    // Determine if metric needs a generated histogram chart
    const renderHistogram = !skipHistogram.includes(id);

    return ReactDOM.createPortal(
        <>
            {/* aria-live region: hidden visually, announced by SRs when opened */}
            <div aria-live="polite" role="status" className="sr-only">{announcement}</div>

            {/* Overlay */}
            <div
                className="fixed inset-0 z-50 bg-black bg-opacity-50"
                // Click outside closes, but keeps focus return behavior
                onMouseDown={(e) => {
                // Only close if the click started on the overlay, not inside the panel
                if (e.target === e.currentTarget) onClose();
                }}
            />

            {/* Dialog */}
            <div
                id={modalId}
                ref={containerRef}
                role="dialog"
                aria-modal="true"
                aria-labelledby={`${modalId}-title`}
                aria-describedby={`${modalId}-desc`}
                className="fixed inset-0 z-[51] flex items-center justify-center"
            >
                <div className="bg-white w-full overflow-y-auto h-full relative transition-transform duration-500 transform">

                {/* Sticky header */}
                <div className="sticky top-0 z-10 flex items-center justify-start bg-lacey-green px-6 py-3 shadow-md">
                    <img className="mr-2" src={metadataIconWhite} alt="Metric Icon" />
                    <h1
                        id={`${modalId}-title`}
                        ref={headingRef}
                        tabIndex={-1}
                        className="text-white text-lg font-bold focus:outline-none"
                    >
                        {/* If we have metadata, use the first metric's name; fallback text otherwise */}
                        {metadata?.[0]?.metric_name ?? 'Metric details'}
                    </h1>
                </div>

                {/* Short description for SR context */}
                <p id={`${modalId}-desc`} ref={descriptionRef} className="sr-only">
                    This panel contains details, sources, race &amp; ethnicity breakdowns, and comparison values. Press Escape to close.
                </p>

                <div className='p-6'>
                {loading ? (
                    <LoadingIndicator />
                ) : error ? (
                    <p className="text-red-500 text-sm">Error fetching metric metadata.</p>
                ) : metadata && metadata.length > 0 ? (
                    <div>
                        {metadata.map((metric) => { 
                            return (
                            <div key={metric.id} className="mb-4">
                                <p className='text-lacey-text text-xs w-11/12'>{metric.definition}</p>
                                {metric.versions && metric.versions.length > 0 && (
                                    <div>
                                        {metric.versions.map((version) => {
                                            const calculatedScope = version.level_of_detail?.trim().toLowerCase();
                                            const sourceScope = version.source_level_of_detail?.trim().toLowerCase();

                                            const showSourceScope =
                                            version.source_level_of_detail &&
                                            sourceScope &&
                                            calculatedScope &&
                                            sourceScope !== calculatedScope;

                                            return (
                                            <div key={version.metric_version_id_ext} className="my-2">
                                                <h2 className='text-lacey-text font-bold text-sm mt-3'>Why Include This Metric?</h2>
                                                <p className='text-lacey-text text-xs w-11/12'>{version.why_important}</p>
                                                {/* Details */}
                                                <dl className="grid grid-cols-2 gap-4 mt-6">
                                                    <div className={`flex flex-col ${showSourceScope ? 'col-span-2': ''}`}>
                                                    <dt className="text-lacey-text font-bold text-sm">Data Year</dt>
                                                    <dd className="text-lacey-text text-xs">{version.year}</dd>
                                                    </div>
                                                    <div className="flex flex-col">
                                                    <dt className="text-lacey-text font-bold text-sm">{showSourceScope ? 'Geographic Level (shown)' : 'Geographic Level'}</dt>
                                                    <dd className="text-lacey-text text-xs">
                                                        {capitalizeFirstLetter(version.level_of_detail)}
                                                    </dd>
                                                    </div>

                                                    {showSourceScope && (
                                                    <div className="flex flex-col">
                                                        <dt className="text-lacey-text font-bold text-sm">Geographic Level (source)</dt>
                                                        <dd className="text-lacey-text text-xs">
                                                        {capitalizeFirstLetter(version.source_level_of_detail!)}
                                                        </dd>
                                                    </div>
                                                    )}
                                                    <div className="col-span-2">
                                                    <dt className="text-lacey-text font-bold text-sm">Source</dt>
                                                    <dd className="text-lacey-text text-xs w-11/12">{version.source}</dd>
                                                    </div>

                                                    {/* Source links */}
                                                    {version.source_url && (
                                                    <div className="col-span-2 -mt-2">
                                                        <dd className="space-y-1 text-xs">
                                                        {parseCommaSeparatedUrls(version.source_url)
                                                            .map((u) => u.trim())
                                                            .filter(Boolean)
                                                            .map((url, idx) => {
                                                            const label = buildLinkLabel(url, { fallback: `Source ${idx + 1}` });
                                                            return (
                                                                <div key={idx} className="w-11/12">
                                                                <a
                                                                    className="break-words underline text-[#337AB7]"
                                                                    href={url}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    aria-label={`${label} (opens in a new tab)`}
                                                                >
                                                                    {label}
                                                                    <span className="sr-only"> (opens in a new tab)</span>
                                                                </a>
                                                                {/* Optional secondary line for raw URL */}
                                                                <div className="text-lacey-text/70 overflow-hidden text-ellipsis">
                                                                    {url}
                                                                </div>
                                                                </div>
                                                            );
                                                            })}
                                                        </dd>
                                                    </div>
                                                    )}
                                                </dl>
                                            </div>
                                        )
                                        })}
                                    </div>
                                )}
                                {/* Indicator Histogram - conditionally rendered based on exclusions */}
                                { renderHistogram && <>
                                <div className='bg-[#F0F0F0]'>
                                    <p className='text-lacey-text text-center font-bold text-xs mt-4 py-2 border-b-[1px]'>{metricName} Histogram</p>

                                    <DistributionChart distributionData={!selectionActive ? null : distributionData} countyDistributionData={countyDistributionData} metricName={metricName} id={id} lod={lod}/>

                                    <p className='text-lacey-text text-[11px] text-center'>{unitDescriptor}</p>

                                    <div className='flex justify-around items-end'>
                                        { selectionActive && <p className='text-lacey-text text-[11px] my-3'>Selected Area: <strong>{formatValue(geoAverage, unit, id)}</strong></p> }

                                        <p className='text-lacey-text text-[11px] my-3'>Thurston County: <strong>{formatValue(countyAverage, cUnit, id)}</strong></p>
                                    </div>
                                </div>
                                <p className='text-lacey-text text-sm font-bold w-11/12 mt-4'>How to read this graph:</p>
                                <p className='text-lacey-text text-xs w-11/12 mt-2'>{`The histogram shows the share of ${lod}s from both the selected area and the total geography that fall within each quintile of data. Quintiles are determined by taking all of the data for each metric, and dividing the data into 5 equal categories. The histogram has two main components - the selected ${lod}s and all ${lod}s in the total geography we are comparing to.`}</p>
                                <p className='text-lacey-text text-xs w-11/12 mt-2'>{`The percentages on the histogram bars refer to the share of ${lod}s from each geography (selected and total) that fall into each of the 5 quintiles. This helps to understand the distribution of the data for the selected area compared to the total geography. The selected area average and total geography average are displayed beneath the chart for a more high level comparison.`}</p>
                                </>
                                }
                            </div>
                            )
                        })}
                    </div>
                ) : (
                    <p>No metadata available.</p>
                )}
                </div>

                {/* Close button last in DOM for focus order */}
                <button
                        className="absolute top-2 right-5 text-white text-3xl font-bold z-50 hover:text-gray-200"
                        onClick={onClose}
                        aria-label="Close modal"
                    >
                        &times;
                </button>

                </div>
            </div>
        </>,
        document.body
    );
};

export default MetricMetadataModal;